import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface TenantContextType {
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  isDemoTenant: boolean;
  isLoading: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const { slug: slugFromUrl } = useParams<{ slug: string }>();
  const { tenantId, tenantSlug } = useAuth();
  const navigate = useNavigate();
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [isDemoTenant, setIsDemoTenant] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Use JWT claim when present, otherwise trust the URL slug (already validated
  // by TenantLayout's DB membership check before this provider mounts).
  const effectiveSlug = tenantSlug ?? slugFromUrl ?? null;

  useEffect(() => {
    // Redirect if JWT slug disagrees with URL slug (copy-pasted / manual edit).
    if (tenantSlug && slugFromUrl && slugFromUrl !== tenantSlug) {
      navigate(`/${tenantSlug}/app`, { replace: true });
      return;
    }

    if (!effectiveSlug) {
      setIsLoading(false);
      return;
    }

    const fetchTenantName = async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, name, is_demo')
        .eq('slug', effectiveSlug)
        .single();

      setTenantName(data?.name ?? null);
      setIsDemoTenant(data?.is_demo ?? false);
      setIsLoading(false);
    };

    fetchTenantName();
  }, [effectiveSlug, tenantSlug, slugFromUrl, navigate]);

  return (
    <TenantContext.Provider value={{ tenantId, tenantSlug: effectiveSlug, tenantName, isDemoTenant, isLoading }}>
      {children}
    </TenantContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
